import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  listProjects,
  getProject,
  createProject,
  updateProject,
  archiveProject,
  type Project,
  type CreateProjectPayload,
  type UpdateProjectPayload,
  type ProjectListParams,
} from '../../services/RecruiterServices/ProjectServices/projectService';

interface ProjectsState {
  list: Project[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  loading: boolean;
  submitting: boolean;
  error: string | null;
  current: Project | null;
  currentLoading: boolean;
}

const initialState: ProjectsState = {
  list: [],
  total: 0,
  page: 1,
  limit: 10,
  totalPages: 1,
  loading: false,
  submitting: false,
  error: null,
  current: null,
  currentLoading: false,
};

export const fetchProjects = createAsyncThunk(
  'projects/fetchAll',
  async (params: ProjectListParams, { rejectWithValue }) => {
    try {
      return await listProjects(params);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message ?? 'Failed to fetch projects');
    }
  },
);

export const fetchProjectById = createAsyncThunk(
  'projects/fetchOne',
  async (id: number, { rejectWithValue }) => {
    try {
      return await getProject(id);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message ?? 'Failed to fetch project');
    }
  },
);

export const addProject = createAsyncThunk(
  'projects/add',
  async (payload: CreateProjectPayload, { rejectWithValue }) => {
    try {
      return await createProject(payload);
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message ?? 'Failed to create project');
    }
  },
);

export const editProject = createAsyncThunk(
  'projects/edit',
  async ({ id, payload }: { id: number; payload: UpdateProjectPayload }, { rejectWithValue }) => {
    try {
      await updateProject(id, payload);
      return { id, payload };
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message ?? 'Failed to update project');
    }
  },
);

export const removeProject = createAsyncThunk(
  'projects/archive',
  async (id: number, { rejectWithValue }) => {
    try {
      await archiveProject(id);
      return id;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data?.message ?? 'Failed to archive project');
    }
  },
);

const projectSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.data;
        state.total = action.payload.meta.total;
        state.page = action.payload.meta.page;
        state.limit = action.payload.meta.limit;
        state.totalPages = action.payload.meta.totalPages;
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchProjectById.pending, (state) => {
        state.currentLoading = true;
      })
      .addCase(fetchProjectById.fulfilled, (state, action) => {
        state.currentLoading = false;
        state.current = action.payload;
      })
      .addCase(fetchProjectById.rejected, (state) => {
        state.currentLoading = false;
      })

      .addCase(addProject.pending, (state) => { state.submitting = true; })
      .addCase(addProject.fulfilled, (state) => { state.submitting = false; })
      .addCase(addProject.rejected, (state) => { state.submitting = false; })

      .addCase(editProject.pending, (state) => { state.submitting = true; })
      .addCase(editProject.fulfilled, (state) => { state.submitting = false; })
      .addCase(editProject.rejected, (state) => { state.submitting = false; })

      .addCase(removeProject.fulfilled, (state, action) => {
        state.list = state.list.filter(p => p.project_id !== action.payload);
      });
  },
});

export default projectSlice.reducer;
